import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  Banknote,
  BarChart3,
  FileText,
  Handshake,
  Images,
  Mail,
  Megaphone,
  Newspaper,
  Palmtree,
  Users,
} from 'lucide-react';
import PremiumCard from '../../components/ui/PremiumCard.jsx';
import VisitsChart from '../../components/admin/VisitsChart.jsx';
import api from '../../services/api.js';
import { getAuditActionLabel, getAuditModuleLabel } from '../../constants/auditLog.js';

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await api.get('/v1/admin/stats');
        if (!cancelled && data.success) setStats(data.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const cards = [
    {
      label: 'Slides activos (carrusel)',
      value: stats?.carousel_active ?? '—',
      icon: Images,
      hint: 'Visibles en la página de inicio',
    },
    {
      label: 'Aviso emergente activo',
      value: stats?.home_popup_active ?? '—',
      icon: Megaphone,
      hint: 'Flyer popup en el home',
    },
    {
      label: 'Noticias publicadas',
      value: stats?.noticias_publicadas ?? '—',
      icon: Newspaper,
      hint: 'Sección de comunicación interna',
    },
    {
      label: 'Sedes vacacionales activas',
      value: stats?.experiencias_activas ?? '—',
      icon: Palmtree,
      hint: 'Visibles en inicio y /sedes-vacacionales',
    },
    {
      label: 'Líneas de crédito activas',
      value: stats?.creditos_activos ?? '—',
      icon: Banknote,
      hint: 'Visibles en inicio y /creditos',
    },
    {
      label: 'Convenios visibles',
      value: stats?.convenios_visibles ?? '—',
      icon: Handshake,
      hint: 'Publicados en /convenios',
    },
    {
      label: 'Documentos normatividad',
      value: stats?.normatividad_visible ?? '—',
      icon: FileText,
      hint: 'Visibles en /normatividad',
    },
    {
      label: 'Miembros entes administrativos',
      value: stats?.entes_admin_activos ?? '—',
      icon: Users,
      hint: 'Visibles en /entes-administrativos',
    },
    {
      label: 'PQRS pendientes',
      value: stats?.contacto_pendientes ?? '—',
      icon: Mail,
      hint: `${stats?.contacto_total ?? 0} solicitudes en total`,
    },
  ];

  const analytics = stats?.analytics;
  const auditRecent = stats?.audit_recent ?? [];

  return (
    <div className="mx-auto max-w-6xl space-y-10">
      <div>
        <h1 className="font-display text-3xl font-bold text-accent-blue md:text-4xl">Panel general</h1>
        <p className="mt-3 max-w-2xl text-slate-600">
          Resumen del contenido, tráfico del sitio y actividad reciente en el panel administrativo.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <PremiumCard variant="light" className="!p-5 sm:!p-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-[#00A63E]" aria-hidden />
              <h2 className="font-display text-lg font-semibold text-[#0d2b7f]">Tráfico del sitio</h2>
            </div>
          </div>
          <div className="mb-6 grid grid-cols-3 gap-3 text-center">
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-2 py-3">
              <p className="text-xs text-slate-500">Hoy</p>
              <p className="font-display text-xl font-bold text-[#0d2b7f]">
                {loading ? '…' : analytics?.today ?? 0}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-2 py-3">
              <p className="text-xs text-slate-500">7 días</p>
              <p className="font-display text-xl font-bold text-[#0d2b7f]">
                {loading ? '…' : analytics?.last_7_days ?? 0}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-2 py-3">
              <p className="text-xs text-slate-500">30 días</p>
              <p className="font-display text-xl font-bold text-[#0d2b7f]">
                {loading ? '…' : analytics?.last_30_days ?? 0}
              </p>
            </div>
          </div>
          <VisitsChart data={analytics?.chart ?? []} loading={loading} />
          {analytics?.top_pages?.length > 0 ? (
            <div className="mt-6 border-t border-slate-200 pt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Páginas más visitadas (7 días)
              </p>
              <ul className="mt-2 space-y-1.5 text-sm">
                {analytics.top_pages.map((row) => (
                  <li key={row.path} className="flex justify-between gap-2 text-slate-700">
                    <span className="truncate font-mono text-xs">{row.path}</span>
                    <span className="shrink-0 font-semibold text-[#0d2b7f]">{row.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </PremiumCard>

        <PremiumCard variant="light" className="!p-5 sm:!p-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-[#00A63E]" aria-hidden />
              <h2 className="font-display text-lg font-semibold text-[#0d2b7f]">Actividad reciente</h2>
            </div>
            <Link
              to="/admin/auditoria"
              className="text-sm font-semibold text-[#1acb12] hover:text-[#0d2b7f]"
            >
              Ver todo
            </Link>
          </div>
          {loading ? (
            <p className="text-sm text-slate-500">Cargando…</p>
          ) : auditRecent.length === 0 ? (
            <p className="text-sm text-slate-500">Aún no hay acciones registradas en el panel.</p>
          ) : (
            <ul className="max-h-72 space-y-3 overflow-y-auto pr-1">
              {auditRecent.map((row) => (
                <li
                  key={row.id}
                  className="rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2.5 text-sm"
                >
                  <p className="font-medium text-slate-800">{row.resumen}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {row.usuario_email || 'Sistema'} · {getAuditModuleLabel(row.modulo)} ·{' '}
                    {getAuditActionLabel(row.accion)} · {formatDateTime(row.fecha_creacion)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </PremiumCard>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <PremiumCard key={c.label} hover fullHeight>
            <div className="flex flex-1 items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-accent-lime">{c.label}</p>
                <p className="mt-4 font-display text-4xl font-bold text-white">
                  {loading ? <span className="animate-pulse text-slate-600">···</span> : c.value}
                </p>
                <p className="mt-3 text-sm leading-snug text-slate-400">{c.hint}</p>
              </div>
              <span className="shrink-0 rounded-xl border border-white/10 bg-[#1acb12]/15 p-3 ring-1 ring-[#1acb12]/30">
                <c.icon className="h-6 w-6 text-accent-lime" />
              </span>
            </div>
          </PremiumCard>
        ))}
      </div>
    </div>
  );
}
